// AgentDashboard — the agent's home dashboard (redesigned 2026-07-04).
//
// A date-range toggle drives the EARNINGS figures (game P&L, per-game
// breakdown, player P&L, downline profit). Live balances, counts and the
// pending-request queue ignore the range — a balance is always "now".
//
//   GET /me-agent/dashboard/tiles?range=X         → the headline tiles
//   GET /me-agent/dashboard/detail?tile=Y&range=X → a tile's modal rows
//
// Rendered inside AdminAppShell (top nav + wallet chip live there).

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import Icon from '@baaligames/shared-ui/components/Icon';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import { useT } from '@baaligames/shared-ui/lib/i18n';

const LEVEL_NAMES: Record<number, string> = { 1: 'Super Master', 2: 'Master', 3: 'Agent' };

// ─── Date range (mirrors the admin dashboard's RangeToggle) ─────────

type Range = 'today' | 'yesterday' | 'week' | 'last_week' | 'month' | 'last_month' | 'all';
const RANGE_OPTS: { key: Range; label: string }[] = [
  { key: 'today', label: 'agent.games.range.today' },
  { key: 'week', label: 'agent.games.range.week' },
  { key: 'month', label: 'agent.games.range.month' },
  { key: 'all', label: 'agent.games.range.all' },
];
const rangeLabel = (r: Range) => RANGE_OPTS.find((o) => o.key === r)?.label ?? 'agent.games.range.all';

function RangeToggle({ value, onChange }: { value: Range; onChange: (v: Range) => void }) {
  const t = useT();
  return (
    <div className="rounded-2xl p-1.5 bg-arena-100/60 dark:bg-white/[0.05] ring-1 ring-inset ring-arena-200/50 dark:ring-white/10">
      <div className="flex flex-wrap gap-1.5 justify-center">
        {RANGE_OPTS.map((o) => (
          <button key={o.key} onClick={() => onChange(o.key)}
            className={`px-3 py-1.5 rounded-full text-[12px] font-semibold whitespace-nowrap transition ${
              value === o.key
                ? 'bg-violet-600 text-white shadow-[0_2px_10px_rgba(139,92,246,0.4)]'
                : 'text-arena-700 hover:text-arena-900 dark:text-neon-200/70 dark:hover:text-white'}`}>
            {t(o.label)}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Formatting helpers ─────────────────────────────────────────────

const rupee = (n: number) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
const signedRupee = (n: number) => (Number(n) < 0 ? `−${rupee(Math.abs(n))}` : `+${rupee(n)}`);
const signClass = (n: number) =>
  Number(n) > 0 ? 'text-emerald-600 dark:text-emerald-400'
  : Number(n) < 0 ? 'text-rose-600 dark:text-rose-400'
  : 'text-neutral-900 dark:text-white';

const GAME_NAMES: Record<string, string> = {
  ludo: 'Ludo', xo: 'X & O', rummy: 'Rummy', chess: 'Chess', carrom: 'Carrom',
  snake_ladder: 'Snake & Ladder', plinko: 'Plinko', lucky7: 'Lucky 7', thimbles: 'Thimbles', cricket: 'Cricket',
};
const gameName = (id: string) => GAME_NAMES[id] || id;

type TileKey = 'game' | 'requests' | 'users' | 'downline' | 'self';
type Tiles = {
  game: { pnl: number };
  requests: { recharge: number; withdrawal: number; net: number };
  users: { count: number; totalBalance: number };
  downline: { childLevel: number; childLabel: string; count: number; totalBalance: number };
  self: { balance: number; partnershipPct: number };
};

// ─── Dashboard ──────────────────────────────────────────────────────

export default function AgentDashboard() {
  const { admin } = useAdminAuth();
  // NOTE: `t` is taken by the tiles state below, so the translator is `tr` here.
  const tr = useT();
  const myLevelName = LEVEL_NAMES[Number(admin?.level ?? 1)] || 'Agent';
  const [range, setRange] = useState<Range>('all');
  const [t, setT] = useState<Tiles | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<TileKey | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try { const d = await adminApi.get<Tiles>(`/admin/me-agent/dashboard/tiles?range=${range}`); if (!cancelled) { setT(d); setErr(null); } }
      catch (e) { if (!cancelled) setErr((e as ApiError).message || tr('agent.home.error.load')); }
    };
    void load();
    const onFocus = () => { void load(); };
    window.addEventListener('focus', onFocus);
    const id = window.setInterval(load, 30_000);
    return () => { cancelled = true; window.removeEventListener('focus', onFocus); window.clearInterval(id); };
  }, [range]);

  if (err) return (
    <div className="max-w-xl mx-auto px-3 py-6 space-y-4">
      <RangeToggle value={range} onChange={setRange} />
      <div className="rounded-2xl bg-rose-500/10 ring-1 ring-inset ring-rose-400/30 text-rose-700 dark:text-rose-200 px-4 py-3 text-sm">{err}</div>
    </div>
  );

  const downlineTitle = t ? `${t.downline.childLabel}${/s$/i.test(t.downline.childLabel) ? '' : 's'}` : 'Downline';

  return (
    <div className="max-w-xl lg:max-w-3xl mx-auto px-3 sm:px-4 py-4 space-y-4">

      {/* Welcome — EMPHASIZES the agent's ROLE (Super Master / Master / Agent) so
          it's the first thing they see; username is a small secondary line. */}
      <div className="flex items-center gap-3 pt-0.5 px-0.5">
        <span className="grid place-items-center w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-[0_5px_18px_-4px_rgba(16,185,129,0.55)] shrink-0">
          <Icon name="workspace_premium" size={26} filled />
        </span>
        <div className="min-w-0">
          <p className="text-[12px] font-medium text-arena-500 dark:text-neon-200/55 leading-none">{tr('agent.home.welcome')}</p>
          <h1 className="text-[26px] sm:text-[30px] font-black leading-tight tracking-tight text-emerald-600 dark:text-emerald-400 mt-1">
            {myLevelName}
          </h1>
          <p className="text-[12px] font-semibold text-arena-500 dark:text-neon-200/70 leading-none mt-0.5">@{admin?.username ?? 'agent'}</p>
        </div>
      </div>

      <RangeToggle value={range} onChange={setRange} />

      {!t ? (
        <div className="grid place-items-center py-16 text-arena-600 dark:text-neon-200/65 text-sm">
          <Icon name="progress_activity" size={26} className="animate-spin" /><p className="mt-2">{tr('common.loading')}</p>
        </div>
      ) : (
        <>
          {/* Hero — the one number that matters, for the chosen period */}
          <button type="button" onClick={() => setOpen('game')}
            className="w-full text-left rounded-3xl p-5 bg-white dark:bg-white/[0.04] ring-1 ring-inset ring-arena-200/60 dark:ring-white/10 shadow-sm hover:shadow-md active:scale-[0.995] transition">
            <div className="flex items-center gap-2.5">
              <span className="grid place-items-center w-9 h-9 rounded-xl bg-violet-100 dark:bg-violet-500/15 text-violet-600 dark:text-violet-300"><Icon name="sports_esports" size={19} /></span>
              <span className="text-[13.5px] font-bold text-neutral-800 dark:text-white/90">{tr('agent.home.hero.title')}</span>
              <Icon name="chevron_right" size={18} className="ml-auto text-arena-400 dark:text-neon-200/60" />
            </div>
            <div className={`mt-3 text-[46px] sm:text-[54px] font-black leading-none whitespace-nowrap ${signClass(t.game.pnl)}`}>{signedRupee(t.game.pnl)}</div>
            <p className="mt-2 text-[12.5px] text-arena-600 dark:text-neon-200/65">
              <span className="font-bold text-violet-600 dark:text-violet-300">{tr(rangeLabel(range))}</span> · {tr('agent.home.hero.tap')}
            </p>
          </button>

          {/* The rest — simple, one number each */}
          <div className="grid grid-cols-2 gap-3">
            <StatTile icon="group" accent="sky" title={tr('agent.home.tile.players')} onClick={() => setOpen('users')}
              big={String(t.users.count)} sub={tr('agent.home.tile.totalBalance', { x: rupee(t.users.totalBalance) })} />

            <StatTile icon="swap_horiz" accent="amber" title={tr('agent.home.tile.requests')} onClick={() => setOpen('requests')}
              big={signedRupee(t.requests.net)} bigClass={signClass(t.requests.net)}
              sub={tr('agent.home.tile.inOut', { in: rupee(t.requests.recharge), out: rupee(t.requests.withdrawal) })} />

            <StatTile icon="account_tree" accent="teal" title={downlineTitle} onClick={() => setOpen('downline')}
              big={String(t.downline.count)} sub={tr('agent.home.tile.totalWorth', { x: rupee(t.downline.totalBalance) })} />

            <StatTile icon="account_circle" accent="emerald" title={tr('agent.home.tile.myAccount')} onClick={() => setOpen('self')}
              big={rupee(t.self.balance)} sub={tr('agent.home.tile.partnership', { x: t.self.partnershipPct })} />
          </div>
        </>
      )}

      {open && <DetailModal tile={open} range={range} downlineTitle={downlineTitle} onClose={() => setOpen(null)} />}
    </div>
  );
}

// ─── Detail modal ───────────────────────────────────────────────────
// z-index sits ABOVE the app nav bar (which lives at z-[10000]) so the
// close button is never hidden behind the top bar.

function DetailModal({ tile, range, downlineTitle, onClose }: { tile: TileKey; range: Range; downlineTitle: string; onClose: () => void }) {
  const t = useT();
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setD(null);
    adminApi.get<any>(`/admin/me-agent/dashboard/detail?tile=${tile}&range=${range}`)
      .then((r) => { if (!cancelled) setD(r); })
      .catch((e) => { if (!cancelled) setErr((e as ApiError).message || t('agent.home.error.detail')); });
    return () => { cancelled = true; };
  }, [tile, range]);

  const TITLES: Record<TileKey, string> = { game: t('agent.home.modal.game'), requests: t('agent.home.tile.requests'), users: t('agent.home.tile.players'), downline: downlineTitle, self: t('agent.home.tile.myAccount') };
  // Requests is a live queue; everything else reflects the chosen period.
  const sub = tile === 'requests' ? t('agent.home.modal.pendingNow') : t(rangeLabel(range));

  return (
    <div className="fixed inset-0 z-[10050] bg-black/60 backdrop-blur-sm grid place-items-end sm:place-items-center sm:p-4" onClick={onClose} role="dialog" aria-modal="true">
      <div className="w-full sm:max-w-2xl h-[92vh] sm:h-auto sm:max-h-[85vh] bg-white dark:bg-arena-900 rounded-t-2xl sm:rounded-2xl flex flex-col shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-arena-200/60 dark:border-white/10 shrink-0">
          <div className="min-w-0">
            <h3 className="font-bold text-[15px] text-neutral-900 dark:text-white leading-tight">{TITLES[tile]}</h3>
            <p className="text-[11.5px] text-arena-500 dark:text-neon-200/55 mt-0.5">
              <Icon name={tile === 'requests' ? 'schedule' : 'event'} size={12} className="inline align-[-1px] mr-0.5" />{sub}
            </p>
          </div>
          <button onClick={onClose} aria-label={t('common.close')} className="-mr-1 p-2 rounded-full text-arena-600 dark:text-neon-200/70 hover:bg-arena-100/60 dark:hover:bg-white/10 active:scale-95">
            <Icon name="close" size={22} />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          {err ? <div className="p-4 text-sm text-rose-600 dark:text-rose-400">{err}</div>
            : !d ? <div className="grid place-items-center py-16 text-arena-600 dark:text-neon-200/60 text-sm"><Icon name="progress_activity" size={24} className="animate-spin" /></div>
            : <TileBody tile={tile} d={d} downlineTitle={downlineTitle} onClose={onClose} />}
        </div>
      </div>
    </div>
  );
}

function TileBody({ tile, d, downlineTitle, onClose }: { tile: TileKey; d: any; downlineTitle: string; onClose: () => void }) {
  if (tile === 'game') return <GameTable d={d} />;
  if (tile === 'downline') return <DownlineTable d={d} title={downlineTitle} onClose={onClose} />;
  if (tile === 'users') return <UsersTable d={d} onClose={onClose} />;
  if (tile === 'requests') return <RequestsTable d={d} />;
  return <SelfList d={d} />;
}

// ─── Reusable sortable + filterable table ───────────────────────────
// Mobile-responsive: horizontal scroll + sticky first column. Tap a
// column header to sort (toggles ↑/↓); the filter box narrows rows; the
// bold Total footer recomputes from the currently-visible rows. Only the
// game-name column is allowed to wrap — every number stays on one line.

const THEAD = 'text-[10px] font-bold uppercase tracking-wide text-arena-500 dark:text-neon-200/55';
const STICKY = 'sticky left-0 bg-white dark:bg-arena-900';
const num = (n: number) => Math.round(Number(n) || 0).toLocaleString('en-IN');
const sum = (rows: any[], k: string) => rows.reduce((a, r) => a + (Number(r[k]) || 0), 0);

type Col = {
  key: string;
  label: string;
  align?: 'left' | 'right';
  sticky?: boolean;
  wrap?: boolean;
  render: (r: any) => React.ReactNode;
  sortVal?: (r: any) => number | string;
  filterText?: (r: any) => string;
  cellClass?: (r: any) => string;
  total?: (rows: any[]) => React.ReactNode;
};

function DataTable({ cols, rows, rowKey, totalLabel, footer, placeholder, onRowClick }: {
  cols: Col[];
  rows: any[];
  rowKey: (r: any, i: number) => React.Key;
  totalLabel?: (rows: any[]) => React.ReactNode;
  footer?: (rows: any[]) => React.ReactNode;
  placeholder?: string;
  /** When set, each row is tappable (opens that person's detail page). */
  onRowClick?: (r: any) => void;
}) {
  const t = useT();
  const [q, setQ] = useState('');
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');

  const query = q.trim().toLowerCase();
  const filtered = query
    ? rows.filter((r) => cols.some((c) => c.filterText && c.filterText(r).toLowerCase().includes(query)))
    : rows;

  const sc = cols.find((c) => c.key === sortKey);
  const sorted = sc && sc.sortVal
    ? [...filtered].sort((a, b) => {
        const av = sc.sortVal!(a), bv = sc.sortVal!(b);
        const cmp = typeof av === 'number' && typeof bv === 'number'
          ? av - bv : String(av).localeCompare(String(bv));
        return dir === 'asc' ? cmp : -cmp;
      })
    : filtered;

  const onSort = (c: Col) => {
    if (!c.sortVal) return;
    if (sortKey === c.key) setDir((x) => (x === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(c.key); setDir('desc'); }
  };

  const canFilter = cols.some((c) => c.filterText);
  const edge = (i: number) => `${i === 0 ? 'pl-4' : ''} ${i === cols.length - 1 ? 'pr-4' : ''}`;
  const align = (c: Col) => (c.align === 'left' ? 'text-left' : 'text-right');

  return (
    <div>
      {canFilter && (
        <div className="sticky top-0 z-10 bg-white dark:bg-arena-900 px-4 pt-3 pb-2">
          <div className="relative">
            <Icon name="search" size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-arena-400 dark:text-neon-200/60" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={placeholder || t('agent.home.table.filter')} inputMode="search"
              className="w-full rounded-xl bg-arena-50 dark:bg-white/5 ring-1 ring-inset ring-arena-200/60 dark:ring-white/10 pl-8 pr-8 py-2 text-[16px] text-neutral-900 dark:text-white placeholder-arena-400 dark:placeholder-neon-200/60 focus:outline-none focus:ring-2 focus:ring-violet-400/60"
            />
            {q && (
              <button onClick={() => setQ('')} aria-label={t('agent.home.table.clearFilter')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-arena-400 dark:text-neon-200/60 hover:bg-arena-100/60 dark:hover:bg-white/10">
                <Icon name="close" size={15} />
              </button>
            )}
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px] border-collapse">
          <thead>
            <tr className={THEAD}>
              {cols.map((c, i) => (
                <th key={c.key} onClick={() => onSort(c)} aria-sort={sortKey === c.key ? (dir === 'asc' ? 'ascending' : 'descending') : undefined}
                    className={`${c.sticky ? STICKY : ''} ${align(c)} px-2 py-2 whitespace-nowrap ${edge(i)} ${c.sortVal ? 'cursor-pointer select-none hover:text-arena-700 dark:hover:text-neon-100' : ''}`}>
                  <span className={`inline-flex items-center gap-0.5 ${c.align === 'left' ? '' : 'flex-row-reverse'}`}>
                    {c.label}
                    {c.sortVal && (
                      <Icon name={sortKey === c.key ? (dir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more'} size={12}
                            className={sortKey === c.key ? 'text-violet-500 dark:text-violet-300' : 'opacity-30'} />
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, ri) => (
              <tr key={rowKey(r, ri)}
                  onClick={onRowClick ? () => onRowClick(r) : undefined}
                  onKeyDown={onRowClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(r); } } : undefined}
                  role={onRowClick ? 'button' : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  className={`border-t border-arena-100 dark:border-white/5 ${onRowClick ? 'cursor-pointer hover:bg-arena-50 dark:hover:bg-white/[0.04] active:bg-arena-100 dark:active:bg-white/[0.07]' : ''}`}>
                {cols.map((c, i) => (
                  <td key={c.key}
                      className={`${c.sticky ? STICKY : ''} ${align(c)} px-2 py-2 ${edge(i)} tabular-nums ${c.wrap ? 'leading-tight' : 'whitespace-nowrap'} ${c.cellClass ? c.cellClass(r) : ''}`}>
                    {c.render(r)}
                  </td>
                ))}
              </tr>
            ))}
            {!sorted.length && (
              <tr><td colSpan={cols.length} className="text-center py-10 text-arena-500 dark:text-neon-200/55">{t('agent.home.table.noMatches')}</td></tr>
            )}
          </tbody>
          <tfoot>
            {footer ? (
              <tr className="border-t-2 border-arena-300 dark:border-white/20">
                <td colSpan={cols.length} className="p-0 bg-arena-50 dark:bg-white/5">{footer(sorted)}</td>
              </tr>
            ) : (
              <tr className="border-t-2 border-arena-300 dark:border-white/20 font-black bg-arena-50 dark:bg-white/5">
                {cols.map((c, i) => (
                  <td key={c.key}
                      className={`${c.sticky ? STICKY + ' !bg-arena-50 dark:!bg-arena-800' : ''} ${align(c)} px-2 py-2.5 ${edge(i)} tabular-nums whitespace-nowrap`}>
                    {i === 0 ? (totalLabel ? totalLabel(sorted) : t('agent.home.table.total')) : (c.total ? c.total(sorted) : '')}
                  </td>
                ))}
              </tr>
            )}
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function GameThumb({ id }: { id: string }) {
  const [err, setErr] = useState(false);
  if (err) return <span className="grid place-items-center w-7 h-7 rounded-md bg-violet-100 dark:bg-violet-500/20 text-violet-700 dark:text-violet-300 text-[12px] font-black shrink-0">{gameName(id)[0]}</span>;
  return <img src={`/games/${id}/Logo.png`} alt="" className="w-7 h-7 rounded-md object-contain shrink-0" onError={() => setErr(true)} />;
}

function GameTable({ d }: { d: any }) {
  const t = useT();
  const cols: Col[] = [
    { key: 'game', label: t('agent.home.game.col.game'), align: 'left', sticky: true, wrap: true,
      render: (g) => <span className="flex items-center gap-2"><GameThumb id={g.gameId} /><span className="font-semibold text-neutral-900 dark:text-white leading-tight max-w-[84px]">{gameName(g.gameId)}</span></span>,
      sortVal: (g) => gameName(g.gameId), filterText: (g) => gameName(g.gameId) },
    { key: 'bet', label: t('agent.home.game.col.bet'), render: (g) => `₹${num(g.bet)}`, sortVal: (g) => g.bet, total: (rows) => `₹${num(sum(rows, 'bet'))}` },
    { key: 'won', label: t('agent.home.game.col.won'), cellClass: () => 'text-emerald-600 dark:text-emerald-400', render: (g) => `₹${num(g.won)}`, sortVal: (g) => g.won, total: (rows) => <span className="text-emerald-600 dark:text-emerald-400">₹{num(sum(rows, 'won'))}</span> },
    { key: 'lost', label: t('agent.home.game.col.lost'), cellClass: () => 'text-rose-600 dark:text-rose-400', render: (g) => `₹${num(g.lost)}`, sortVal: (g) => g.lost, total: (rows) => <span className="text-rose-600 dark:text-rose-400">₹{num(sum(rows, 'lost'))}</span> },
    { key: 'myPct', label: t('agent.home.game.col.myPct'), cellClass: () => 'font-semibold text-neutral-900 dark:text-white', render: (g) => `${g.myPct}%`, sortVal: (g) => g.myPct, total: () => '—' },
    { key: 'net', label: t('agent.home.game.col.earned'), cellClass: (g) => `font-bold ${signClass(g.netPnl)}`, render: (g) => signedRupee(g.netPnl), sortVal: (g) => g.netPnl, total: (rows) => <span className={signClass(sum(rows, 'netPnl'))}>{signedRupee(sum(rows, 'netPnl'))}</span> },
    { key: 'toUpline', label: t('agent.home.game.col.toUpline'), cellClass: () => 'text-arena-700 dark:text-neon-200/75', render: (g) => `₹${num(g.toUpline)}`, sortVal: (g) => g.toUpline, total: (rows) => `₹${num(sum(rows, 'toUpline'))}` },
  ];
  if (!d.rows.length) return <Empty msg={t('agent.home.game.empty')} />;
  return <DataTable cols={cols} rows={d.rows} rowKey={(g) => g.gameId} placeholder={t('agent.home.game.filter')} />;
}

function DownlineTable({ d, title, onClose }: { d: any; title: string; onClose: () => void }) {
  const t = useT();
  const nav = useNavigate();
  if (!d.rows.length) return <Empty msg={t('agent.home.downline.empty', { x: title.toLowerCase() })} />;
  const cols: Col[] = [
    { key: 'name', label: t('agent.home.downline.col.name'), align: 'left', sticky: true, cellClass: () => 'font-semibold text-neutral-900 dark:text-white whitespace-nowrap', render: (r) => <span className="inline-flex items-center gap-1">{r.name}<Icon name="chevron_right" size={14} className="text-arena-400 dark:text-neon-200/50 shrink-0" /></span>, sortVal: (r) => r.name, filterText: (r) => r.name },
    { key: 'players', label: t('agent.home.tile.players'), cellClass: () => 'font-semibold text-neutral-900 dark:text-white', render: (r) => String(r.players), sortVal: (r) => r.players, total: (rows) => String(sum(rows, 'players')) },
    { key: 'childPct', label: t('agent.home.downline.col.theirPct'), render: (r) => `${r.childPct}%`, sortVal: (r) => r.childPct, total: () => '—' },
    { key: 'myProfit', label: t('agent.home.downline.col.youEarn'), cellClass: (r) => `font-bold ${signClass(r.myProfit)}`, render: (r) => signedRupee(r.myProfit), sortVal: (r) => r.myProfit, total: (rows) => <span className={signClass(sum(rows, 'myProfit'))}>{signedRupee(sum(rows, 'myProfit'))}</span> },
    { key: 'childProfit', label: t('agent.home.downline.col.theyEarn'), cellClass: () => 'text-arena-700 dark:text-neon-200/75', render: (r) => `₹${num(r.childProfit)}`, sortVal: (r) => r.childProfit, total: (rows) => `₹${num(sum(rows, 'childProfit'))}` },
    { key: 'worth', label: t('agent.home.downline.col.balance'), cellClass: () => 'font-bold text-neutral-900 dark:text-white', render: (r) => `₹${num(r.balance)}`, sortVal: (r) => r.balance, total: (rows) => `₹${num(sum(rows, 'balance'))}` },
  ];
  return <DataTable cols={cols} rows={d.rows} rowKey={(r) => r.agentId}
                    onRowClick={(r) => { onClose(); nav(`/people/agents/${r.agentId}`); }}
                    totalLabel={(rows) => t('agent.home.table.totalCount', { n: rows.length })} placeholder={t('agent.home.downline.filter', { x: title.toLowerCase() })} />;
}

function UsersTable({ d, onClose }: { d: any; onClose: () => void }) {
  const t = useT();
  const nav = useNavigate();
  if (!d.rows.length) return <Empty msg={t('agent.home.users.empty')} />;
  const cols: Col[] = [
    { key: 'name', label: t('agent.home.users.col.player'), align: 'left', sticky: true, cellClass: () => 'font-semibold text-neutral-900 dark:text-white whitespace-nowrap', render: (r) => <span className="inline-flex items-center gap-1">{r.name}<Icon name="chevron_right" size={14} className="text-arena-400 dark:text-neon-200/50 shrink-0" /></span>, sortVal: (r) => r.name, filterText: (r) => r.name },
    { key: 'recharged', label: t('agent.home.requests.recharge'), cellClass: () => 'text-emerald-600 dark:text-emerald-400', render: (r) => `+₹${num(r.recharged)}`, sortVal: (r) => r.recharged, total: (rows) => <span className="text-emerald-600 dark:text-emerald-400">+₹{num(sum(rows, 'recharged'))}</span> },
    { key: 'withdrawn', label: t('agent.home.requests.withdrawal'), cellClass: () => 'text-rose-600 dark:text-rose-400', render: (r) => `−₹${num(r.withdrawn)}`, sortVal: (r) => r.withdrawn, total: (rows) => <span className="text-rose-600 dark:text-rose-400">−₹{num(sum(rows, 'withdrawn'))}</span> },
    { key: 'netPnl', label: t('agent.home.users.col.winLoss'), cellClass: (r) => `font-bold ${signClass(r.netPnl)}`, render: (r) => signedRupee(r.netPnl), sortVal: (r) => r.netPnl, total: (rows) => <span className={signClass(sum(rows, 'netPnl'))}>{signedRupee(sum(rows, 'netPnl'))}</span> },
    { key: 'balance', label: t('agent.home.downline.col.balance'), cellClass: () => 'font-bold text-neutral-900 dark:text-white', render: (r) => `₹${num(r.balance)}`, sortVal: (r) => r.balance, total: (rows) => `₹${num(sum(rows, 'balance'))}` },
  ];
  return <DataTable cols={cols} rows={d.rows} rowKey={(r) => r.userId} totalLabel={(rows) => t('agent.home.table.totalCount', { n: rows.length })} placeholder={t('agent.home.users.filter')} />;
}

function RequestsTable({ d }: { d: any }) {
  const t = useT();
  if (!d.rows.length) return <Empty msg={t('agent.home.requests.empty')} />;
  const cols: Col[] = [
    { key: 'who', label: t('agent.home.requests.col.from'), align: 'left', sticky: true,
      cellClass: () => 'whitespace-nowrap',
      render: (r) => <span><span className="font-semibold text-neutral-900 dark:text-white">{r.who}</span> <span className="text-[10px] text-arena-500 dark:text-neon-200/60">{r.src}</span></span>,
      sortVal: (r) => r.who, filterText: (r) => `${r.who} ${r.src} ${r.kind}` },
    { key: 'kind', label: t('agent.home.requests.col.type'), align: 'left', cellClass: (r) => `font-semibold ${r.kind === 'recharge' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`, render: (r) => (r.kind === 'recharge' ? t('agent.home.requests.recharge') : t('agent.home.requests.withdrawal')), sortVal: (r) => r.kind, filterText: (r) => (r.kind === 'recharge' ? t('agent.home.requests.recharge') : t('agent.home.requests.withdrawal')) },
    { key: 'amount', label: t('agent.home.requests.col.amount'), cellClass: (r) => `font-bold ${r.kind === 'recharge' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`, render: (r) => `${r.kind === 'recharge' ? '+' : '−'}₹${num(r.amount)}`, sortVal: (r) => (r.kind === 'recharge' ? 1 : -1) * (Number(r.amount) || 0) },
  ];
  const footer = (rows: any[]) => {
    const rc = rows.filter((r) => r.kind === 'recharge').reduce((a, r) => a + (Number(r.amount) || 0), 0);
    const wd = rows.filter((r) => r.kind !== 'recharge').reduce((a, r) => a + (Number(r.amount) || 0), 0);
    const net = rc - wd;
    return (
      <div className="px-4 py-3 grid grid-cols-3 gap-2 text-center">
        <FootStat label={t('agent.home.requests.recharge')} value={`+₹${num(rc)}`} cls="text-emerald-600 dark:text-emerald-400" />
        <FootStat label={t('agent.home.requests.withdrawal')} value={`−₹${num(wd)}`} cls="text-rose-600 dark:text-rose-400" />
        <FootStat label={t('agent.home.requests.net')} value={signedRupee(net)} cls={signClass(net)} />
      </div>
    );
  };
  return <DataTable cols={cols} rows={d.rows} rowKey={(_r, i) => i} footer={footer} placeholder={t('agent.home.requests.filter')} />;
}

function SelfList({ d }: { d: any }) {
  const fmt = (r: any) =>
    r.kind === 'money' ? `₹${num(r.value)}`
    : r.kind === 'pct' ? `${r.value}%`
    : r.kind === 'signed' ? signedRupee(r.value)
    : String(r.value);
  return (
    <div className="p-4 space-y-2">
      {d.rows.map((r: any, i: number) => (
        <div key={i} className="flex items-center justify-between py-2.5 border-b border-arena-100 dark:border-white/5 last:border-0">
          <span className="text-[13px] text-arena-600 dark:text-neon-200/70">{r.label}</span>
          <span className={`text-[15px] font-bold whitespace-nowrap ${r.kind === 'signed' ? signClass(r.value) : 'text-neutral-900 dark:text-white'}`}>{fmt(r)}</span>
        </div>
      ))}
    </div>
  );
}

function FootStat({ label, value, cls }: { label: string; value: string; cls: string }) {
  return <div><div className="text-[10px] font-bold uppercase tracking-wide text-arena-500 dark:text-neon-200/55">{label}</div><div className={`text-[15px] font-black whitespace-nowrap ${cls}`}>{value}</div></div>;
}
function Empty({ msg }: { msg: string }) {
  return <div className="grid place-items-center py-16 text-arena-500 dark:text-neon-200/55 text-sm gap-2"><Icon name="inbox" size={26} /><p>{msg}</p></div>;
}

// ─── Tiles ──────────────────────────────────────────────────────────

const ACCENTS: Record<string, string> = {
  violet:  'text-violet-600 dark:text-violet-300 bg-violet-50 dark:bg-violet-500/10',
  amber:   'text-amber-600 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10',
  sky:     'text-sky-600 dark:text-sky-300 bg-sky-50 dark:bg-sky-500/10',
  teal:    'text-teal-600 dark:text-teal-300 bg-teal-50 dark:bg-teal-500/10',
  emerald: 'text-emerald-600 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/10',
};

function StatTile({ icon, title, big, sub, accent, bigClass, onClick }: {
  icon: string; title: string; big: string; sub: string; accent: string; bigClass?: string; onClick?: () => void;
}) {
  return (
    <button type="button" onClick={onClick}
      className="text-left w-full rounded-2xl bg-white dark:bg-white/[0.04] ring-1 ring-inset ring-arena-200/60 dark:ring-white/10 p-4 shadow-sm hover:ring-arena-300 dark:hover:ring-white/20 hover:shadow-md active:scale-[0.99] transition-all">
      <div className="flex items-center gap-2 mb-2.5">
        <span className={`grid place-items-center w-7 h-7 rounded-lg ${ACCENTS[accent] || ACCENTS.violet}`}><Icon name={icon} size={15} /></span>
        <h3 className="font-bold text-[12.5px] text-neutral-800 dark:text-white/90 truncate">{title}</h3>
        <Icon name="chevron_right" size={16} className="ml-auto text-arena-400 dark:text-neon-200/60 shrink-0" />
      </div>
      <div className={`text-[26px] font-black leading-none whitespace-nowrap ${bigClass || 'text-neutral-900 dark:text-white'}`}>{big}</div>
      <div className="mt-1.5 text-[11.5px] text-arena-600 dark:text-neon-200/60 truncate">{sub}</div>
    </button>
  );
}
